import { useState, useEffect } from 'react';
import { useJwt, useAuthHeaders } from '@campus/core';
import { useWebhookStore } from '../../stores/webhookStore.js';
import type { Webhook } from '../../stores/webhookStore.js';
import { API_BASE } from '../../lib/apiBase.js';
import { Badge, Button, Input, Select } from '../ds/index.js';

const EVENT_TYPES = [
  { value: 'zone.entered', label: 'Entrée dans la zone' },
  { value: 'zone.exited', label: 'Sortie de la zone' },
  { value: 'bubble.entered', label: 'Entrée dans une bulle' },
  { value: 'bubble.exited', label: "Sortie d'une bulle" },
];

function getTenantIdFromJwt(jwt: string | null): string {
  if (!jwt) return '';
  try {
    const payload = JSON.parse(atob(jwt.split('.')[1]?.replace(/-/g, '+').replace(/_/g, '/') ?? ''));
    return (payload.tid as string) ?? '';
  } catch { return ''; }
}

const formatDate = (iso: string) =>
  new Date(iso).toLocaleString('fr-FR', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });

/**
 * WebhooksPanel — onglet « Webhooks » de la fenêtre Apps (ADMIN/OWNER).
 * Création, révocation, historique des livraisons par webhook.
 * Le secret HMAC n'est affiché qu'une fois, juste après la création.
 */
export function WebhooksPanel() {
  const jwt = useJwt();
  const tenantId = getTenantIdFromJwt(jwt);
  const authHeaders = useAuthHeaders(tenantId);

  const webhooks = useWebhookStore((s) => s.webhooks);
  const deliveries = useWebhookStore((s) => s.deliveries);
  const loading = useWebhookStore((s) => s.loading);
  const error = useWebhookStore((s) => s.error);
  const fetchWebhooks = useWebhookStore((s) => s.fetchWebhooks);
  const createWebhook = useWebhookStore((s) => s.createWebhook);
  const revokeWebhook = useWebhookStore((s) => s.revokeWebhook);
  const fetchDeliveries = useWebhookStore((s) => s.fetchDeliveries);

  const [url, setUrl] = useState('');
  const [zoneSlug, setZoneSlug] = useState('');
  const [eventType, setEventType] = useState('zone.entered');
  const [creating, setCreating] = useState(false);
  const [newSecret, setNewSecret] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  // Le store ne garde les livraisons que d'un webhook à la fois.
  const [openHistoryId, setOpenHistoryId] = useState<string | null>(null);
  const [zoneList, setZoneList] = useState<Array<{ slug: string; displayName: string }>>([]);

  useEffect(() => {
    if (jwt && tenantId) void fetchWebhooks(jwt, tenantId);
  }, [jwt, tenantId, fetchWebhooks]);

  useEffect(() => {
    if (!jwt || !tenantId) return;
    fetch(`${API_BASE}/world/zones`, { headers: authHeaders })
      .then((res) => res.json())
      .then((data: Array<{ slug: string; displayName: string }>) => {
        const zones = data.map((z) => ({ slug: z.slug, displayName: z.displayName }));
        setZoneList(zones);
        setZoneSlug((cur) => cur || zones[0]?.slug || '');
      })
      .catch(() => setZoneList([]));
  }, [jwt, tenantId]);

  const zoneName = (slug: string) => zoneList.find((z) => z.slug === slug)?.displayName ?? slug;

  async function handleCreate() {
    if (!jwt || !url.trim() || !zoneSlug.trim()) return;
    setCreating(true);
    setNewSecret(null);
    setCopied(false);
    const hook = await createWebhook(jwt, tenantId, { url: url.trim(), zoneSlug: zoneSlug.trim(), eventType });
    if (hook?.secret) setNewSecret(hook.secret);
    if (hook) setUrl('');
    setCreating(false);
  }

  function toggleHistory(id: string) {
    if (openHistoryId === id) { setOpenHistoryId(null); return; }
    setOpenHistoryId(id);
    if (jwt && tenantId) void fetchDeliveries(jwt, tenantId, id);
  }

  async function copySecret() {
    if (!newSecret) return;
    try { await navigator.clipboard.writeText(newSecret); setCopied(true); } catch { /* sélection manuelle */ }
  }

  const active = webhooks.filter((h) => h.active !== false);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <p className="pl-lead">Préviens un autre outil quand quelqu'un entre ou sort d'une zone ou d'une bulle. Réservé aux admins.</p>

      {newSecret && (
        <div className="pl-alert pl-alert-warning" role="status" style={{ flexDirection: 'column', gap: 10 }}>
          <strong>Copie le secret de signature maintenant</strong>
          <span style={{ fontWeight: 400, color: 'var(--color-text-secondary)' }}>
            Il sert à vérifier l'en-tête <code>X-Campus-Signature</code>. Il ne sera plus jamais affiché.
          </span>
          <code style={{ display: 'block', width: '100%', boxSizing: 'border-box', padding: '8px 10px', borderRadius: 'var(--radius-md)', background: 'var(--color-bg-raised)', font: '700 12px/1.4 var(--font-mono)', overflowWrap: 'anywhere', userSelect: 'all' }}>{newSecret}</code>
          <div style={{ display: 'flex', gap: 8, alignSelf: 'flex-end' }}>
            <Button variant="secondary" size="sm" onClick={() => { void copySecret(); }}>{copied ? 'Copié' : 'Copier'}</Button>
            <Button variant="primary" size="sm" onClick={() => setNewSecret(null)}>C'est noté</Button>
          </div>
        </div>
      )}

      <section aria-label="Nouveau webhook" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <Input label="URL cible" type="url" value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://exemple.fr/webhook" />
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 12 }}>
          {zoneList.length > 0 ? (
            <Select label="Zone" value={zoneSlug} onChange={(e) => setZoneSlug(e.target.value)} options={zoneList.map((z) => ({ value: z.slug, label: z.displayName }))} />
          ) : (
            <Input label="Zone" value={zoneSlug} onChange={(e) => setZoneSlug(e.target.value)} placeholder="Identifiant de la zone" />
          )}
          <Select label="Événement" value={eventType} onChange={(e) => setEventType(e.target.value)} options={EVENT_TYPES} />
        </div>
        {error && <div className="pl-alert" role="alert">{error}</div>}
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button variant="primary" onClick={() => { void handleCreate(); }} disabled={creating || !url.trim() || !zoneSlug.trim()}>
            {creating ? 'Création…' : 'Créer le webhook'}
          </Button>
        </div>
      </section>

      <section aria-label="Webhooks actifs" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <h3 className="pl-section-title">
          Webhooks actifs <span style={{ font: '700 11px/1 var(--font-mono)', color: 'var(--color-text-muted)' }}>{loading ? '…' : active.length}</span>
        </h3>
        {!loading && active.length === 0 && (
          <p className="pl-lead" style={{ color: 'var(--color-text-muted)' }}>Aucun webhook pour l'instant.</p>
        )}
        {active.map((hook) => (
          <WebhookCard
            key={hook.id}
            hook={hook}
            zoneName={zoneName(hook.zoneSlug)}
            historyOpen={openHistoryId === hook.id}
            deliveries={openHistoryId === hook.id ? deliveries : []}
            onToggleHistory={() => toggleHistory(hook.id)}
            onRevoke={() => { if (jwt && window.confirm('Révoquer ce webhook ? Il ne recevra plus aucun événement.')) void revokeWebhook(jwt, tenantId, hook.id); }}
          />
        ))}
      </section>
    </div>
  );
}

function WebhookCard({ hook, zoneName, historyOpen, deliveries, onToggleHistory, onRevoke }: {
  hook: Webhook;
  zoneName: string;
  historyOpen: boolean;
  deliveries: ReturnType<typeof useWebhookStore.getState>['deliveries'];
  onToggleHistory: () => void;
  onRevoke: () => void;
}) {
  const eventLabel = EVENT_TYPES.find((e) => e.value === hook.eventType)?.label ?? hook.eventType;
  return (
    <article className="pl-card" style={{ flexDirection: 'column', alignItems: 'stretch', gap: 12, padding: 14 }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
        <div style={{ minWidth: 0, display: 'flex', flexDirection: 'column', gap: 6 }}>
          <span style={{ font: '700 12px/1.4 var(--font-mono)', overflowWrap: 'anywhere' }}>{hook.url}</span>
          <span style={{ fontSize: 12, color: 'var(--color-text-secondary)' }}>{eventLabel} · {zoneName}</span>
        </div>
        <Badge tone="success">Actif</Badge>
      </div>

      {historyOpen && (
        deliveries.length === 0 ? (
          <p style={{ margin: 0, fontSize: 12, color: 'var(--color-text-muted)' }}>Aucune livraison pour l'instant.</p>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
            <caption style={{ textAlign: 'left', paddingBottom: 6, fontWeight: 500, color: 'var(--color-text-secondary)' }}>Dernières livraisons</caption>
            <tbody>
              {deliveries.slice(0, 6).map((d) => {
                const ok = d.status === 'success';
                const code = d.lastError?.match(/^HTTP (\d{3})/)?.[1];
                return (
                  <tr key={d.id} style={{ borderTop: '1px solid var(--color-border-subtle)' }}>
                    <td style={{ padding: '6px 0', width: 90 }}>
                      <Badge tone={ok ? 'success' : 'danger'}>{ok ? 'Livré' : code ? `Échec ${code}` : 'Échec'}</Badge>
                    </td>
                    <td style={{ padding: '6px 0', color: 'var(--color-text-secondary)', fontVariantNumeric: 'tabular-nums' }} title={d.lastError ?? undefined}>
                      {formatDate(d.deliveredAt ?? d.createdAt)}
                    </td>
                    <td style={{ padding: '6px 0', textAlign: 'right', color: 'var(--color-text-muted)' }}>
                      {d.attempts <= 1 ? '1re tentative' : `${d.attempts}e tentative${!ok && d.attempts >= 5 ? ', abandonné' : ''}`}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )
      )}

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
        <Button variant="ghost" size="sm" onClick={onToggleHistory} aria-expanded={historyOpen}>
          {historyOpen ? "Masquer l'historique" : "Voir l'historique"}
        </Button>
        <Button variant="danger" size="sm" onClick={onRevoke}>Révoquer</Button>
      </div>
    </article>
  );
}
